import { create } from 'zustand';
import type { DropView, FindView, NothingReason, ResolveResult } from '../models/types';
import { api } from '../services/api';
import { ApiException } from '../services/apiClient';
import { currentPosition } from '../services/geolocation';
import { Logger } from '../services/logger';
import { POOR_SIGNAL_MESSAGE } from '../services/messages';
import { usePlayer } from './player';

const log = new Logger('Scan');

export type ScanPhase = 'idle' | 'scanning' | 'found' | 'nothing' | 'resolving' | 'error';

/** Client-side mirror of the server cooldown so the button disables immediately. */
export const SCAN_COOLDOWN_SECONDS = 30;

const nowSeconds = () => Math.floor(Date.now() / 1000);

/** What a drop pickup did, for the toast; `error` when the request failed outright. */
export type DropPickupOutcome = { pickedUp: number; unavailable: number } | { error: string };

/** "✨ Picked up 2 items! 1 already gone." / "Someone got there first." */
export function pickupMessage(pickedUp: number, unavailable: number): string {
  if (pickedUp === 0) return 'Someone got there first.';
  const text = `✨ Picked up ${pickedUp} ${pickedUp === 1 ? 'item' : 'items'}!`;
  return unavailable > 0 ? `${text} ${unavailable} already gone.` : text;
}

interface ScanState {
  phase: ScanPhase;
  find: FindView | null;
  nothingReason: NothingReason | null;
  nextWindowAt: number | null;
  /** Unix seconds until which the Scan button is disabled. */
  cooldownUntil: number | null;
  errorMessage: string | null;
  lastResolve: ResolveResult | null;
  /** Drops within reach of the last successful scan, newest first. */
  drops: DropView[];
  /** A drop pickup request is in flight. */
  pickingUp: boolean;
  scan: () => Promise<void>;
  /** Pick up these drops from a fresh position; null when one is already in flight. */
  pickupDrops: (dropIds: string[]) => Promise<DropPickupOutcome | null>;
  resolve: (opts: { pickup: boolean }) => Promise<ResolveResult | null>;
  dismiss: () => void;
  reset: () => void;
}

export const isBusy = (s: Pick<ScanState, 'phase'>): boolean => s.phase === 'scanning' || s.phase === 'resolving';
export const cooldownRemaining = (cooldownUntil: number | null, now: number): number =>
  cooldownUntil == null ? 0 : Math.max(0, cooldownUntil - now);

const initial = {
  phase: 'idle' as ScanPhase,
  find: null,
  nothingReason: null,
  nextWindowAt: null,
  cooldownUntil: null,
  errorMessage: null,
  lastResolve: null,
  drops: [] as DropView[],
  pickingUp: false,
};

export const useScan = create<ScanState>((set, get) => ({
  ...initial,

  scan: async () => {
    if (isBusy(get())) return;
    set({ phase: 'scanning', find: null, errorMessage: null });
    try {
      const pos = await currentPosition();
      const result = await api.scan({ lat: pos.latitude, lng: pos.longitude, accuracy: pos.accuracy });
      const cooldownUntil = nowSeconds() + SCAN_COOLDOWN_SECONDS;
      // Each successful scan replaces the list (an empty one hides the section).
      set({ drops: result.drops ?? [] });
      if (result.kind === 'found') {
        log.info('found', { itemId: result.find.itemId, rarity: result.find.rarity, partId: result.find.partId });
        set({ phase: 'found', find: result.find, cooldownUntil });
      } else {
        set({ phase: 'nothing', nothingReason: result.reason, nextWindowAt: result.nextWindowAt, cooldownUntil });
      }
      // Scan updates stats on the profile; realtime will also push this.
      void usePlayer.getState().refresh();
    } catch (e) {
      if (e instanceof ApiException) {
        if (e.isCooldown) {
          set({ phase: 'idle', cooldownUntil: nowSeconds() + (e.retryAfterSeconds ?? SCAN_COOLDOWN_SECONDS) });
        } else if (e.isAccuracyTooLow) {
          set({ phase: 'error', errorMessage: POOR_SIGNAL_MESSAGE });
        } else {
          log.warn('scan failed', { error: e });
          set({ phase: 'error', errorMessage: e.message });
        }
      } else {
        log.error('scan failed', { error: e });
        set({ phase: 'error', errorMessage: 'Could not scan right now. Check your connection and GPS.' });
      }
    }
  },

  resolve: async ({ pickup }) => {
    const find = get().find;
    if (!find || isBusy(get())) return null;
    set({ phase: 'resolving' });
    try {
      const result = await api.resolveFind(find.findId, { pickup });
      set({ phase: 'idle', find: null, lastResolve: result });
      void usePlayer.getState().refresh();
      return result;
    } catch (e) {
      if (e instanceof ApiException && e.isHuntExpired) {
        set({ phase: 'error', find: null, errorMessage: 'That hunt ran out of time.' });
        // The hunt is gone server-side; drop it from the screen too.
        void usePlayer.getState().refresh();
      } else if (e instanceof ApiException) {
        set({ phase: 'error', find: null, errorMessage: e.isFindNotFound ? 'That find drifted away. Scan again!' : e.message });
      } else {
        log.error('resolve failed', { error: e });
        set({ phase: 'error', errorMessage: 'Could not reach the Ethr. Try again.' });
      }
      return null;
    }
  },

  pickupDrops: async (dropIds) => {
    if (get().pickingUp || dropIds.length === 0) return null;
    set({ pickingUp: true });
    try {
      const pos = await currentPosition();
      const result = await api.pickupDrops(dropIds, { lat: pos.latitude, lng: pos.longitude, accuracy: pos.accuracy });
      // Every requested id comes back either picked up (as a new inventory record, which
      // carries no drop id) or unavailable, so all of them leave the list.
      const gone = new Set([...dropIds, ...result.unavailable]);
      set({ drops: get().drops.filter((d) => !gone.has(d.dropId)), pickingUp: false });
      void usePlayer.getState().refresh();
      return { pickedUp: result.pickedUp.length, unavailable: result.unavailable.length };
    } catch (e) {
      set({ pickingUp: false });
      if (e instanceof ApiException) {
        log.warn('drop pickup failed', { error: e });
        return { error: e.isAccuracyTooLow ? POOR_SIGNAL_MESSAGE : e.message };
      }
      log.error('drop pickup failed', { error: e });
      return { error: 'Could not pick that up right now. Check your connection and GPS.' };
    }
  },

  dismiss: () => set({ phase: 'idle', find: null, errorMessage: null }),
  reset: () => set({ ...initial }),
}));
